/* globals window */
import React, { useState, useEffect } from 'react';
import {
  FormWithRedirect,
  SelectInput,
  useGetList,
  useRedirect,
  useNotify,
} from 'react-admin';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import InfoIcon from '@material-ui/icons/Info';
import ServiceVehicleSettings from '../ServiceVehicleSettings';
import Scheduling from './Scheduling';
import { getScheduleStats } from './scheduleApiHelpers';
import { CalendarEditAvailabilityProvider } from '../../Context';
import { getZ3PConfigValue, getServiceEntity } from '../../helper';
import ConfirmActionModal from '../../components/ConfirmActionModal';

const useStyles = makeStyles(() => ({
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    margin: '8px 0',
    position: 'relative',
    '& .MuiFormHelperText-contained': {
      display: 'none',
    },
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 5,
    color: '#949393',
    cursor: 'pointer',
  },
  ml5: {
    marginLeft: 5,
  },
  serviceSelect: {
    '& .MuiFormLabel-filled': {
      display: 'none',
    },
    '& .MuiSelect-root': {
      border: '1px solid #949393',
      backgroundColor: '#ffffff',
      padding: '7px 20px 7px 10px',
    },
    '& .MuiInput-root': {
      fontSize: '1.125rem',
      fontWeight: '600',
    },
  },
}));

const EditAvailability = () => {
  const classes = useStyles();
  const redirect = useRedirect();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [start, setStart] = useState(new Date().setDate(0));
  const [end, setEnd] = useState(
    new Date().setMonth(new Date().getMonth() + 3),
  );
  const [recurringDayDetails, setRecurringDayDetails] = useState([]);
  const [allScheduleTypes, setAllScheduleTypes] = useState([]);
  const [allClientLocations, setAllClientLocations] = useState([]);
  const [scheduleExceptions, setScheduleExceptions] = useState([]);
  const [refreshExceptionTable, setRefreshExceptionTable] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showExceptionsList, setShowExceptionsList] = useState(false);
  const [showExceptionsOnCalendar, setShowExceptionsOnCalendar] = useState(
    true,
  );
  const [showAvailabilityOnCalendar, setShowAvailabilityOnCalendar] = useState(
    true,
  );
  const [showCompactSettings, setShowCompactSettings] = useState(false);
  const [isValidToSubmit, setIsValidToSubmit] = useState(false);
  const [singleDayBooleanHasChanged, setSingleDayBooleanHasChanged] = useState(
    false,
  );
  const [hasSingleDayTimeBlockError, setHasSingleDayTimeBlockError] = useState(
    false,
  );
  const [recurringDayErrors, setRecurringDayErrors] = useState(
    new Array(7).fill(false),
  );
  const [selectedRecurringDays, setSelectedRecurringDays] = useState(
    new Array(7).fill(false),
  );
  const [copiedTimeBlocks, setCopiedTimeBlocks] = useState([]);
  const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState(false);
  const serviceLocations = getZ3PConfigValue('service_locations');
  const hasWorkplaceService = serviceLocations?.includes('workplace');
  const { data: serviceVehicles, loading: serviceVehiclesLoading } = useGetList(
    'service-vehicle',
    { page: 1, perPage: 1000 },
    { field: 'id', order: 'ASC' },
    { is_active: true },
  );
  const notify = useNotify();

  const handleSelectDate = (val, vehicleId = null) => {
    const dateSelected = new Date(val);
    if (dateSelected < start || end < dateSelected) {
      const startDate = new Date(val).setDate(0);
      const endDate = new Date(val).setMonth(new Date(val).getMonth() + 3);
      setStart(startDate);
      setEnd(endDate);
      const serviceVehicleId = selectedVehicle ? selectedVehicle.id : vehicleId;
      getScheduleStats(serviceVehicleId, startDate, endDate).then((data) => {
        setScheduleData(data);
        setSelectedDate(dateSelected);
      });
    } else {
      setSelectedDate(dateSelected);
    }
  };

  useEffect(() => {
    // When redirecting to this page from the calendar, grab query params to set service vehicle + date
    const hashString = !selectedVehicle && window?.location?.hash;
    if (
      hashString &&
      hashString.includes('?') &&
      hashString.includes('serviceVehicleId') &&
      hashString.includes('selectedDate')
    ) {
      const queryString = decodeURIComponent(
        window.location.hash.split('?')[1],
      );
      const queryParams = JSON.parse(queryString);
      const vehicleId = queryParams.serviceVehicleId;
      const serviceEntity = getServiceEntity();
      // Only allowed params are date and service vehicle, so only grab those values
      if (queryParams.selectedDate) {
        handleSelectDate(queryParams.selectedDate, vehicleId);
      }
      if (vehicleId) {
        if (!selectedVehicle && Object.values(serviceVehicles).length > 0) {
          const matchingVehicle = Object.values(serviceVehicles).find(
            (v) => v.id === vehicleId,
          );
          if (matchingVehicle) {
            setSelectedVehicle(matchingVehicle);
          } else {
            notify(
              `This ${serviceEntity} is inactive. Please select another ${serviceEntity}.`,
              'error',
            );
            setSelectedVehicle(Object.values(serviceVehicles)[0]);
          }
        }
      }
    } else if (!selectedVehicle && Object.values(serviceVehicles).length > 0) {
      setSelectedVehicle(
        Object.values(serviceVehicles).sort((a, b) =>
          a.name > b.name ? 1 : -1,
        )[0],
      );
    }
  }, [selectedVehicle, serviceVehicles]);

  useEffect(() => {
    let active = true;
    if (selectedVehicle?.id && active) {
      getScheduleStats(selectedVehicle.id, start, end).then(setScheduleData);
    }
    // Use this active flag as a cleanup to avoid race conditions
    return () => (active = false);
  }, [selectedVehicle, start, end]);

  const handleMonthChange = (val) => {
    setStart(new Date(val).setDate(0));
    setEnd(new Date(val).setMonth(new Date(val).getMonth() + 1));
  };

  const handleVehicleSelect = (val) => {
    const vehicle = serviceVehicles[val.target.value];
    setSelectedVehicle(vehicle);
  };

  const handleBackToCalendarClick = () => {
    if (isValidToSubmit || singleDayBooleanHasChanged) {
      setOpenUnsavedChangesModal(true);
    } else {
      redirect(
        `/schedule?editAvailabilityDate=${moment(selectedDate).format(
          'YYYY-MM-DD',
        )}`,
      );
    }
  };

  const contextValues = {
    selectedVehicle,
    setSelectedVehicle,
    scheduleData,
    setScheduleData,
    handleMonthChange,
    start,
    end,
    recurringDayDetails,
    setRecurringDayDetails,
    allScheduleTypes,
    setAllScheduleTypes,
    allClientLocations,
    setAllClientLocations,
    scheduleExceptions,
    setScheduleExceptions,
    refreshExceptionTable,
    setRefreshExceptionTable,
    selectedDate,
    setSelectedDate,
    hasWorkplaceService,
    handleSelectDate,
    showExceptionsList,
    setShowExceptionsList,
    showExceptionsOnCalendar,
    setShowExceptionsOnCalendar,
    showAvailabilityOnCalendar,
    setShowAvailabilityOnCalendar,
    isValidToSubmit,
    setIsValidToSubmit,
    singleDayBooleanHasChanged,
    setSingleDayBooleanHasChanged,
    hasSingleDayTimeBlockError,
    setHasSingleDayTimeBlockError,
    recurringDayErrors,
    setRecurringDayErrors,
    selectedRecurringDays,
    setSelectedRecurringDays,
    copiedTimeBlocks,
    setCopiedTimeBlocks,
  };
  return (
    <CalendarEditAvailabilityProvider value={contextValues}>
      <ConfirmActionModal
        open={openUnsavedChangesModal}
        mainText={`You have unsaved changes. To save, click Cancel and then ${
          isValidToSubmit ? 'Set Weekly' : 'Set Single Day'
        }. Otherwise, click Discard Changes.`}
        confirmText="Discard Changes"
        onConfirm={() => {
          redirect(
            `/schedule?editAvailabilityDate=${moment(selectedDate).format(
              'YYYY-MM-DD',
            )}`,
          );
        }}
        onCancel={(e) => {
          e.stopPropagation();
          setOpenUnsavedChangesModal(false);
        }}
      />
      <Grid container justify="center">
        <Grid item xs={12}>
          <Box>
            <Box className={classes.modalHeader}>
              <Button
                size="small"
                aria-label="back"
                className={classes.backButton}
                onClick={handleBackToCalendarClick}
              >
                <ArrowBackIcon />
                <Typography variant="body2" className={classes.ml5}>
                  Back to Calendar
                </Typography>
              </Button>
              {serviceVehiclesLoading ? (
                <Box style={{ height: '80px' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  style={{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'baseline',
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    style={{ marginRight: '10px' }}
                  >
                    Set Availability for
                  </Typography>
                  <FormWithRedirect
                    render={() => (
                      <SelectInput
                        variant="standard"
                        source="service-vehicle"
                        choices={Object.values(serviceVehicles).sort((a, b) =>
                          a.name > b.name ? 1 : -1,
                        )}
                        optionText="name"
                        optionValue="id"
                        onChange={handleVehicleSelect}
                        defaultValue={selectedVehicle?.id}
                        className={classes.serviceSelect}
                      />
                    )}
                  />
                  <InfoIcon
                    style={{ marginLeft: '5px', cursor: 'pointer' }}
                    color="disabled"
                    onClick={() => setShowCompactSettings((prev) => !prev)}
                  />
                </Box>
              )}
            </Box>
            {selectedVehicle && (
              <Box>
                <ServiceVehicleSettings
                  selectedVehicle={selectedVehicle}
                  showCompactSettings={showCompactSettings}
                  compact
                />
                <Paper>
                  <Scheduling />
                </Paper>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </CalendarEditAvailabilityProvider>
  );
};

export default EditAvailability;
